import type { ReactNode } from "react";

import { Icon } from "@/components/ui/icon";
import { SkeletonContainer, SkeletonLabel } from "@/components/ui/skeleton";
import { pluralize } from "@/lib/string/pluralize";
import { cn } from "@/lib/utils";

import type { EstimateState } from "./types";

function ReviewSummaryRoot({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-contrast/[0.02] p-2.5 text-[13px]">
      {children}
    </div>
  );
}

function ReviewSummaryRow({
  label,
  value,
  truncate = false,
}: {
  label: string;
  value: string;
  /** Keep a long value on one line, cut with an ellipsis. */
  truncate?: boolean;
}) {
  return (
    <div
      className={cn("flex items-center justify-between", truncate && "gap-3")}
    >
      <span className={cn(truncate && "shrink-0", "text-muted-foreground")}>
        {label}
      </span>
      <span
        className={cn(
          truncate && "min-w-0 truncate",
          "font-medium text-foreground",
        )}
      >
        {value}
      </span>
    </div>
  );
}

export const ReviewSummary = Object.assign(ReviewSummaryRoot, {
  Row: ReviewSummaryRow,
});

function CostBreakdownRoot({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-contrast/[0.02] p-2.5 text-[13px]">
      {children}
    </div>
  );
}

const COST_LINE_TONES = {
  charge: "font-medium text-foreground tabular-nums",
  saving: "font-medium text-success tabular-nums dark:text-[#66DC7E]",
  none: "font-medium text-foreground",
};

function CostBreakdownLine({
  label,
  tone = "charge",
  children,
}: {
  label: ReactNode;
  /** `none` is a line with no charge. */
  tone?: keyof typeof COST_LINE_TONES;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={COST_LINE_TONES[tone]}>{children}</span>
    </div>
  );
}

function CostBreakdownTotal({
  credits,
  plainWhenFree = false,
}: {
  credits: number;
  /** Show a free export as a plain "0 credits": "−0 credits" in red reads
   * as a charge. */
  plainWhenFree?: boolean;
}) {
  const charged = credits > 0 || !plainWhenFree;
  return (
    <div className="mt-0.5 flex items-center justify-between border-t border-border pt-1.5">
      <span className="font-medium text-foreground">Total</span>
      <span
        className={cn(
          "font-semibold tabular-nums",
          charged ? "text-destructive" : "text-foreground",
        )}
      >
        {plainWhenFree ? (
          <>
            {charged && <>&minus;</>}
            {credits} {pluralize(credits, "credit")}
          </>
        ) : (
          `−${credits} ${pluralize(credits, "credit")}`
        )}
      </span>
    </div>
  );
}

export const CostBreakdown = Object.assign(CostBreakdownRoot, {
  Line: CostBreakdownLine,
  Total: CostBreakdownTotal,
});

/** The cost section of a review step: a skeleton while the estimate loads,
 * and a notice above the cost when it was priced without the estimate. */
export function CostPreview({
  state,
  lines,
  children,
}: {
  state: EstimateState;
  /** Cost lines the skeleton stands in for. */
  lines: number;
  children: ReactNode;
}) {
  if (state === "loading") return <CostPreviewSkeleton lines={lines} />;
  return (
    <>
      {state === "unavailable" && (
        <ReviewNotice role="alert">
          We couldn&apos;t load the exact cost of this export, so this is an
          estimate. The final cost may differ.
        </ReviewNotice>
      )}
      {children}
    </>
  );
}

/** The cost lines, total and balance change while the estimate loads. */
function CostPreviewSkeleton({ lines }: { lines: number }) {
  return (
    <SkeletonContainer role="status" className="flex-col gap-3">
      <span className="sr-only">Calculating the cost of this export…</span>
      <div className="flex flex-col gap-1 rounded-lg border border-border bg-contrast/[0.02] p-2.5">
        {Array.from({ length: lines }, (_, i) => (
          <div key={i} className="flex h-[19.5px] items-center justify-between">
            <SkeletonLabel className="h-3 w-36" />
            <SkeletonLabel className="h-3 w-16" />
          </div>
        ))}
        <div className="mt-0.5 flex h-[26.5px] items-center justify-between border-t border-border pt-1.5">
          <SkeletonLabel className="h-3 w-10" />
          <SkeletonLabel className="h-3 w-20" />
        </div>
      </div>
      <div className="flex h-[42px] items-center justify-between rounded-lg border border-border bg-contrast/2 px-3">
        <SkeletonLabel className="h-3 w-36" />
        <SkeletonLabel className="h-3 w-24" />
      </div>
    </SkeletonContainer>
  );
}

/** Balance before → after the export; `after` below zero is a shortfall. */
export function BalanceChange({
  before,
  after,
}: {
  before: number;
  after: number;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-contrast/2 px-3 py-2.5">
      <span className="text-xs font-medium text-muted-foreground">
        Changes to your balance
      </span>
      <span className="flex items-center gap-2 text-sm">
        <span className="flex items-center gap-1 text-muted-foreground">
          <Icon
            name="IconCoin1"
            size={13}
            className="text-[#c9682a] dark:text-[#FFAF53]"
          />
          <span className="font-medium tabular-nums">
            {before.toLocaleString()}
          </span>
        </span>
        <Icon
          name="IconArrowRight"
          size={13}
          className="text-foreground opacity-40"
        />
        <span
          className={cn(
            "flex items-center gap-1",
            after >= 0 ? "text-heading" : "text-destructive",
          )}
        >
          <Icon
            name="IconCoin1"
            size={13}
            className="text-[#c9682a] dark:text-[#FFAF53]"
          />
          <span className="font-semibold tabular-nums">
            {after.toLocaleString()}
          </span>
        </span>
      </span>
    </div>
  );
}

export function NotEnoughCredits({ shortfall }: { shortfall: number }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-border bg-contrast/2 px-3 py-2.5">
      <div className="flex flex-col gap-0.5">
        <span className="flex items-center gap-1 text-xs font-medium text-destructive">
          <Icon name="IconExclamationCircle" className="size-4" />
          Not enough credits
        </span>
        <span className="text-xs text-muted-foreground">
          You need{" "}
          <b className="font-medium text-foreground">
            {/* Four text nodes like the copies this replaced: a different
                split shifts the glyphs by a subpixel. */}
            {shortfall.toLocaleString()}
            {" more"}
            {" "}
            {pluralize(shortfall, "credit")}
          </b>{" "}
          to export.
        </span>
      </div>
    </div>
  );
}

export function ReviewNotice({
  role,
  children,
}: {
  role?: "alert";
  children: ReactNode;
}) {
  return (
    <div
      role={role}
      className="flex items-start gap-2 rounded-lg border border-[#FFAF53]/40 bg-[#FFAF53]/8 px-3 py-2.5 text-xs leading-relaxed text-foreground"
    >
      <Icon
        name="IconExclamationCircle"
        size={14}
        className="mt-px shrink-0 text-[#c9682a] dark:text-[#FFAF53]"
      />
      <span>{children}</span>
    </div>
  );
}
